import * as React from "react";
import axios, { isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateDocument } from "@/hooks/useCreateDocument";
import { useDocument } from "@/hooks/useDocument";
import { useUser } from "@/hooks/useUser";
import { ICreateDocumentRequest } from "@/types/document.types";

export const useDocumentCreationOnError = () => {
  const { user } = useUser();
  const { data: document, error: documentError } = useDocument();
  const { mutate: createDocument, isPending: isCreating } = useCreateDocument();
  const queryClient = useQueryClient();

  // Track if we've already attempted to create a document for this 404 error
  const documentCreationAttemptedRef = React.useRef<boolean>(false);

  // Handle 404 error and create document
  React.useEffect(() => {
    // Reset the flag when the document is successfully loaded or when there's no error
    if (document || !documentError) {
      documentCreationAttemptedRef.current = false;
    }

    if (
      documentError &&
      isAxiosError(documentError) &&
      documentError?.response?.status === 404 &&
      user &&
      !isCreating &&
      !documentCreationAttemptedRef.current // Only create if we haven't already attempted
    ) {
      // Mark that we're attempting to create a document immediately
      documentCreationAttemptedRef.current = true;

      const createNewDocument = async () => {
        try {
          const { getDocumentInfo } = await import("@/utils/document.utils");
          const { getDocumentFile, getDocumentText } = await import("@/taskpane/taskpane");
          const { convertDocxToSfdt, getDocument } = await import("@/services/document.service");

          const [docInfo, plainText] = await Promise.all([getDocumentInfo(), getDocumentText()]);
          const docxBlob = await getDocumentFile().catch(() => null);

          // Convert .docx to SFDT so the web app can render this document
          const sfdtContent = docxBlob ? await convertDocxToSfdt(docxBlob) : "";

          const createPayload: ICreateDocumentRequest = {
            editor_id: user.id,
            title: docInfo.name,
            content: sfdtContent || "",
            collaborators: [user.id],
            doc_type: "ms_word",
            ms_word_id: docInfo.id,
          };

          createDocument(createPayload, {
            onSuccess: async (data) => {
              // Upload plain text to upload_text_url for text_content.
              // `plainText === null` means Word.run failed; skip the upload
              // rather than wiping the fresh S3 slot with an empty string.
              if (data.document_id != null && plainText != null) {
                try {
                  const newDocument = await getDocument(data.document_id);
                  if (newDocument.upload_text_url != null && newDocument.upload_text_url !== "") {
                    // Use bare axios (not axiosInstance) because upload_text_url is a
                    // pre-signed S3 URL — it must NOT carry our Authorization header.
                    await axios.put(newDocument.upload_text_url, plainText, {
                      headers: { "Content-Type": "text/plain" },
                    });
                  }
                } catch (error) {
                  console.error("Failed to upload text content:", error);
                }
              }
              // Invalidate after both SFDT (stored on create) and text upload are done
              queryClient.invalidateQueries({ queryKey: ["document"] });
            },
            onError: () => {
              // Reset the flag on error so user can retry if needed
              documentCreationAttemptedRef.current = false;
            },
          });
        } catch (error) {
          console.error("Failed to create document:", error);
          // Reset the flag on error so user can retry if needed
          documentCreationAttemptedRef.current = false;
        }
      };

      createNewDocument();
    }
  }, [documentError, user, createDocument, isCreating, document, queryClient]);
};
